import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileTextIcon as FileText,
  PlusIcon as Plus,
  PrinterIcon as Printer,
  TrashIcon as Trash,
  DownloadSimpleIcon as DownloadSimple,
  ArrowLeftIcon as ArrowLeft,
  SparkleIcon as Sparkle,
  FilePdfIcon as FilePdf,
  FolderIcon as Folder,
} from "@phosphor-icons/react";
import {
  fetchDeliverableTypes,
  fetchAgents,
  createDeliverable,
  listDeliverables,
  getDeliverable,
  updateDeliverable,
  deleteDeliverable,
  decideDeliverable,
  listWorkspaces,
  listWorkspaceMembers,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Markdown from "@/components/Markdown";
import ValidationBadge from "@/components/validation/ValidationBadge";
import DeliverableValidationPanel from "@/components/deliverables/DeliverableValidationPanel";
import { formatDecided } from "@/lib/validationConstants";
import { KNOWLEDGE_HINT, UNVALIDATED_TOGGLE_LABEL, knowledgeSummary, profileLineForType } from "@/lib/knowledgeContext";

export default function Deliverables() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [types, setTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [list, setList] = useState([]);
  const [view, setView] = useState("list"); // list | new | detail
  const [active, setActive] = useState(null);

  const [form, setForm] = useState({ deliverable_type: "", title: "", context: "", agent_id: "", workspace_id: "", include_unvalidated: false });
  const [generating, setGenerating] = useState(false);

  const refreshList = () => listDeliverables().then(setList);

  useEffect(() => {
    Promise.all([
      fetchDeliverableTypes(),
      fetchAgents(),
      listDeliverables(),
      listWorkspaces(),
    ]).then(([t, a, d, w]) => {
      setTypes(t);
      setAgents(a);
      setList(d);
      setWorkspaces(w);
      // Deep-link support: /deliverables?open=<id>
      const openId = searchParams.get("open");
      if (openId) {
        getDeliverable(openId).then((full) => { setActive(full); setView("detail"); }).catch(() => {});
      }
    });
  }, []);

  const startNew = () => {
    const defaultType = types[0];
    setForm({
      deliverable_type: defaultType?.id || "",
      title: "",
      context: "",
      agent_id: defaultType?.default_agent || "architect",
      workspace_id: "",
      include_unvalidated: false,
    });
    setView("new");
  };

  const onTypeChange = (id) => {
    const t = types.find((x) => x.id === id);
    setForm((f) => ({ ...f, deliverable_type: id, agent_id: t?.default_agent || f.agent_id }));
  };

  const submitGenerate = async () => {
    if (!form.deliverable_type || !form.title.trim() || !form.context.trim()) {
      toast.error("Please complete type, title, and context.");
      return;
    }
    setGenerating(true);
    try {
      const payload = { ...form };
      if (!payload.workspace_id) {
        delete payload.workspace_id;
        delete payload.include_unvalidated;
      }
      const created = await createDeliverable(payload);
      await refreshList();

      // Poll for completion (background generation can take 30-120s).
      let final = created;
      const startedAt = Date.now();
      const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
      while (final.status === "generating") {
        if (Date.now() - startedAt > TIMEOUT_MS) {
          throw new Error("Generation timed out after 5 minutes.");
        }
        await new Promise((r) => setTimeout(r, 3000));
        final = await getDeliverable(created.id);
      }
      if (final.status === "failed") {
        throw new Error(final.error || "Generation failed.");
      }
      await refreshList();
      setActive(final);
      setView("detail");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const openDetail = async (id) => {
    const d = await getDeliverable(id);
    setActive(d);
    setView("detail");
    setSearchParams({ open: id }, { replace: true });
  };

  const backToList = () => {
    setView("list");
    setActive(null);
    setSearchParams({}, { replace: true });
  };

  const handleAssignWorkspace = async (workspaceId) => {
    if (!active) return;
    await updateDeliverable(active.id, { workspace_id: workspaceId || null });
    const updated = await getDeliverable(active.id);
    setActive(updated);
    await refreshList();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this deliverable?")) return;
    await deleteDeliverable(id);
    await refreshList();
    if (active?.id === id) {
      setView("list");
      setActive(null);
    }
  };

  const [deciding, setDeciding] = useState(false);
  const handleDecide = async (decision, note) => {
    if (!active) return;
    setDeciding(true);
    try {
      const updated = await decideDeliverable(active.id, decision, note);
      setActive(updated);
      await refreshList();
      toast.success(
        decision === "validated" ? "Deliverable validated." : decision === "rejected" ? "Deliverable rejected." : "Reopened as AI inference.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the decision.");
    } finally {
      setDeciding(false);
    }
  };

  const handleDownload = () => {
    if (!active) return;
    const blob = new Blob([active.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.title.replace(/[^a-z0-9]+/gi, "_")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if (!active) return;
    const element = document.getElementById("deliverable-print-target");
    if (!element) return;
    const { exportElementToPdf } = await import("@/lib/pdfExport");
    const filename = `${active.title.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
    await exportElementToPdf(element, filename);
    if (active.workspace_id) {
      const { logWorkspaceEvent } = await import("@/lib/api");
      logWorkspaceEvent(active.workspace_id, "single_deliverable_pdf_exported", {
        deliverable_id: active.id,
        title: active.title,
      });
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="deliverables-page">
      {view === "list" && (
        <ListView
          types={types}
          list={list}
          workspaces={workspaces}
          onNew={startNew}
          onOpen={openDetail}
          onDelete={handleDelete}
        />
      )}
      {view === "new" && (
        <NewView
          types={types}
          agents={agents}
          workspaces={workspaces}
          form={form}
          setForm={setForm}
          onTypeChange={onTypeChange}
          onCancel={backToList}
          onSubmit={submitGenerate}
          generating={generating}
        />
      )}
      {view === "detail" && active && (
        <DetailView
          deliverable={active}
          types={types}
          agents={agents}
          workspaces={workspaces}
          onBack={backToList}
          onDownload={handleDownload}
          onDownloadPdf={handleDownloadPdf}
          onAssignWorkspace={handleAssignWorkspace}
          onDelete={() => handleDelete(active.id)}
          onDecide={handleDecide}
          deciding={deciding}
        />
      )}
    </div>
  );
}

function ListView({ types, list, workspaces, onNew, onOpen, onDelete }) {
  const wsMap = Object.fromEntries(workspaces.map((w) => [w.id, w]));
  return (
    <div data-testid="deliverables-list-view">
      <div className="mb-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="ml-eyebrow mb-2">Deliverable Generator</div>
          <h1 className="text-3xl lg:text-5xl font-semibold tracking-tight">
            Generate structured deliverables.
            <br />
            <span className="text-[var(--ml-text-secondary)]">Assessments. Roadmaps. API specs.</span>
          </h1>
        </div>
        <div className="md:col-span-4 md:text-right">
          <button onClick={onNew} className="ml-btn-primary" data-testid="new-deliverable-btn">
            <Plus size={16} weight="bold" /> New Deliverable
          </button>
        </div>
      </div>

      {/* Type catalogue */}
      <div className="mb-12">
        <div className="ml-eyebrow mb-4">Available Templates ({types.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {types.map((t) => (
            <div key={t.id} className="ml-card p-4" data-testid={`deliverable-type-${t.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <FileText size={16} weight="bold" color="#0f62fe" />
                <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                  {t.id.replace(/_/g, " ")}
                </span>
              </div>
              <div className="font-medium text-[15px] leading-tight mb-1">{t.name}</div>
              <div className="text-xs text-[var(--ml-text-secondary)] leading-relaxed">
                {t.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Existing deliverables */}
      <div>
        <div className="ml-eyebrow mb-4">Your Deliverables ({list.length})</div>
        {list.length === 0 ? (
          <div className="ml-card p-10 text-center" data-testid="empty-deliverables">
            <FileText size={28} color="#0f62fe" weight="duotone" className="mx-auto mb-3" />
            <div className="font-medium mb-1">No deliverables yet</div>
            <p className="text-sm text-[var(--ml-text-secondary)] mb-5">
              Generate your first modernization assessment in under a minute.
            </p>
            <button onClick={onNew} className="ml-btn-primary" data-testid="empty-new-deliverable-btn">
              <Plus size={16} weight="bold" /> New Deliverable
            </button>
          </div>
        ) : (
          <div className="ml-card divide-y divide-[var(--ml-border)]">
            {list.map((d) => (
              <div
                key={d.id}
                className="p-4 lg:p-5 flex items-center justify-between gap-4 hover:bg-[var(--ml-surface)]/50 cursor-pointer"
                onClick={() => onOpen(d.id)}
                data-testid={`deliverable-row-${d.id}`}
              >
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-9 h-9 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center shrink-0">
                    <FileText size={16} weight="bold" color="#0f62fe" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {d.title}
                      <ValidationBadge state={d.validation_state} row={d} testid={`deliverable-state-${d.id}`} />
                      {d.status === "generating" && (
                        <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[var(--ml-warning)]/20 text-[#8a6d00] border border-[var(--ml-warning)]/40">
                          Generating
                        </span>
                      )}
                      {d.status === "failed" && (
                        <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[var(--ml-error)]/10 text-[var(--ml-error)] border border-[var(--ml-error)]/30">
                          Failed
                        </span>
                      )}
                    </div>
                    <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex flex-wrap items-center gap-x-2">
                      <span>{d.deliverable_type.replace(/_/g, " ")} · {new Date(d.created_at).toLocaleString()}</span>
                      {d.workspace_id && wsMap[d.workspace_id] && (
                        <span className="inline-flex items-center gap-1 text-[var(--ml-primary)]">
                          <Folder size={11} weight="bold" /> {wsMap[d.workspace_id].name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    className="ml-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}
                    data-testid={`delete-deliverable-${d.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewView({ types, agents, workspaces, form, setForm, onTypeChange, onCancel, onSubmit, generating }) {
  return (
    <div data-testid="deliverables-new-view">
      <button onClick={onCancel} className="ml-btn-ghost mb-6" data-testid="cancel-new-deliverable">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="ml-eyebrow mb-2">Generate</div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-2">
            New Deliverable
          </h1>
          <p className="text-[var(--ml-text-secondary)] mb-8 max-w-xl">
            Choose a template, give it a title, and provide enough business/system context so the agent
            can produce a credible, useful document. The more specific you are, the better the output.
          </p>

          <div className="space-y-5">
            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-2">
                Deliverable Type
              </label>
              <select
                value={form.deliverable_type}
                onChange={(e) => onTypeChange(e.target.value)}
                className="ml-input"
                data-testid="deliverable-type-select"
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-2">
                  Authoring Agent
                </label>
                <select
                  value={form.agent_id}
                  onChange={(e) => setForm((f) => ({ ...f, agent_id: e.target.value }))}
                  className="ml-input"
                  data-testid="deliverable-agent-select"
                >
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-2">
                  Workspace (optional)
                </label>
                <select
                  value={form.workspace_id || ""}
                  onChange={(e) => setForm((f) => ({ ...f, workspace_id: e.target.value }))}
                  className="ml-input"
                  data-testid="deliverable-workspace-select"
                >
                  <option value="">— None —</option>
                  {workspaces.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.workspace_id && (
              <div
                className="border border-[var(--ml-border)] bg-[var(--ml-bg-secondary)] px-3 py-2.5 text-xs text-[var(--ml-text-secondary)] space-y-2"
                data-testid="deliverable-knowledge-hint"
              >
                <p>{KNOWLEDGE_HINT}</p>
                {profileLineForType(types.find((t) => t.id === form.deliverable_type)) && (
                  <p className="text-[var(--ml-text-primary)]" data-testid="deliverable-knowledge-profile">
                    {profileLineForType(types.find((t) => t.id === form.deliverable_type))}
                  </p>
                )}
                <label className="flex items-start gap-2 text-[var(--ml-text-primary)] cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(form.include_unvalidated)}
                    onChange={(e) => setForm((f) => ({ ...f, include_unvalidated: e.target.checked }))}
                    data-testid="deliverable-include-unvalidated-toggle"
                  />
                  <span>{UNVALIDATED_TOGGLE_LABEL}</span>
                </label>
              </div>
            )}

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-2">
                Title
              </label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="ml-input"
                placeholder="e.g. Acme Distribution — Order Management Modernization"
                data-testid="deliverable-title-input"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-2">
                Business / System Context
              </label>
              <textarea
                value={form.context}
                onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
                rows={10}
                className="ml-textarea"
                placeholder="Describe the business problem, ERP/IBM i environment, key processes, stakeholders, constraints, and goals. The more concrete, the better."
                data-testid="deliverable-context-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={onSubmit}
                disabled={generating}
                className="ml-btn-primary"
                data-testid="generate-deliverable-btn"
              >
                {generating ? (
                  <>Generating <span className="ml-dot" /><span className="ml-dot" /><span className="ml-dot" /></>
                ) : (
                  <><Sparkle size={16} weight="bold" /> Generate Deliverable</>
                )}
              </button>
              <button onClick={onCancel} className="ml-btn-secondary" disabled={generating}>
                Cancel
              </button>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="ml-card p-5 lg:sticky lg:top-24">
            <div className="ml-eyebrow mb-3">Tips for a great deliverable</div>
            <ul className="space-y-2.5 text-sm text-[var(--ml-text-primary)]">
              {[
                "Name the ERP / IBM i environment (e.g. Infor XA v9 on IBM i 7.4).",
                "List one or two key business processes in scope.",
                "Identify stakeholders and decision-makers.",
                "Mention any constraints — budget, timeline, regulatory.",
                "State what 'success' looks like in business terms.",
              ].map((t, i) => (
                <li key={t} className="flex gap-2.5">
                  <span className="ml-mono text-xs text-[var(--ml-primary)] shrink-0 mt-0.5">0{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-[var(--ml-border)] text-xs text-[var(--ml-text-secondary)]">
              Generation typically takes 20–60 seconds depending on length and template.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function DetailView({ deliverable, types, agents, workspaces, onBack, onDownload, onDownloadPdf, onAssignWorkspace, onDelete, onDecide, deciding }) {
  const { user } = useAuth();
  const [myRole, setMyRole] = useState(null);
  useEffect(() => {
    if (!deliverable.workspace_id) { setMyRole(null); return; }
    listWorkspaceMembers(deliverable.workspace_id)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [deliverable.workspace_id, user]);
  const canEdit = user?.role === "admin" || !deliverable.workspace_id || myRole === "owner" || myRole === "editor";
  const t = types.find((x) => x.id === deliverable.deliverable_type);
  const a = agents.find((x) => x.id === deliverable.agent_id);
  const w = workspaces.find((x) => x.id === deliverable.workspace_id);
  const decided = formatDecided(deliverable);
  const knowledge = knowledgeSummary(deliverable.knowledge_context);
  return (
    <div data-testid="deliverables-detail-view">
      <div className="flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button onClick={onBack} className="ml-btn-ghost" data-testid="back-to-deliverables-list">
          <ArrowLeft size={14} /> All Deliverables
        </button>
        <div className="flex gap-2 flex-wrap">
          <select
            value={deliverable.workspace_id || ""}
            onChange={(e) => onAssignWorkspace(e.target.value)}
            className="ml-input py-1.5 text-sm min-w-[180px]"
            data-testid="detail-workspace-select"
            title="Assign to workspace"
          >
            <option value="">— No workspace —</option>
            {workspaces.map((wsp) => (
              <option key={wsp.id} value={wsp.id}>{wsp.name}</option>
            ))}
          </select>
          {deliverable.deliverable_type === "abap_heatmap" && deliverable.workspace_id && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "")}/api/workspaces/${deliverable.workspace_id}/sap-ecc/heatmap/pdf?deliverable_id=${encodeURIComponent(deliverable.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-btn-primary"
              data-testid="download-heatmap-branded-pdf-btn"
              title="Download the branded heat-map PDF (cover page + risk table + AI narrative)"
            >
              <FilePdf size={16} weight="bold" /> Branded PDF
            </a>
          )}
          <button onClick={onDownloadPdf} className="ml-btn-primary" data-testid="download-pdf-btn">
            <FilePdf size={16} weight="bold" /> Save PDF
          </button>
          <button onClick={onDownload} className="ml-btn-secondary" data-testid="download-deliverable-btn">
            <DownloadSimple size={16} /> .md
          </button>
          <button onClick={() => window.print()} className="ml-btn-secondary" data-testid="print-deliverable-btn">
            <Printer size={16} /> Print
          </button>
          <button onClick={onDelete} className="ml-btn-ghost text-[var(--ml-error)]" data-testid="detail-delete-deliverable-btn">
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div id="deliverable-print-target" className="bg-white">
        <header className="mb-8 border-b border-[var(--ml-border)] pb-6">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            {t?.name || deliverable.deliverable_type} · authored by {a?.name || deliverable.agent_id}
            {w && <span> · workspace: {w.name}</span>}
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight mt-2">{deliverable.title}</h1>
          <div className="ml-mono text-xs text-[var(--ml-text-secondary)] mt-2 flex flex-wrap items-center gap-2">
            <span>{new Date(deliverable.created_at).toLocaleString()}</span>
            <ValidationBadge state={deliverable.validation_state} row={deliverable} testid="detail-validation-badge" />
          </div>
          <div
            className="text-xs mt-3 px-3 py-2 border"
            style={deliverable.validation_state === "validated"
              ? { background: "#defbe6", borderColor: "#a7f0ba", color: "#0e6027" }
              : deliverable.validation_state === "rejected"
                ? { background: "#f4f4f4", borderColor: "#c6c6c6", color: "#525252" }
                : { background: "#f6f2ff", borderColor: "#d4bbff", color: "#6929c4" }}
            data-testid="detail-validation-line"
          >
            {deliverable.validation_state === "validated"
              ? <>Consultant-validated by {decided}{deliverable.validation_note ? ` — ${deliverable.validation_note}` : ""}.</>
              : deliverable.validation_state === "rejected"
                ? <>Rejected by {decided}{deliverable.validation_note ? ` — ${deliverable.validation_note}` : ""}. Do not distribute.</>
                : <>AI-generated inference — not yet validated by a consultant. Treat findings and recommendations as hypotheses.</>}
          </div>
          {knowledge && (
            <div
              className="text-xs mt-2 px-3 py-2 border border-[var(--ml-border)] bg-[var(--ml-bg-secondary)] text-[var(--ml-text-secondary)]"
              data-testid="detail-knowledge-line"
            >
              <span className="text-[var(--ml-text-primary)]">{knowledge.note}</span> {knowledge.grounded}
              {knowledge.profileLine && <> <span data-testid="detail-knowledge-profile">{knowledge.profileLine}</span></>}
              {knowledge.hints.length > 0 && <> Named objects from the request: {knowledge.hints.join(", ")}.</>}
              {knowledge.truncated && <> Knowledge context was truncated to stay within the prompt budget.</>}
              {knowledge.includeUnvalidated && <> Proposed rules were included at the author&rsquo;s request, labelled PROPOSED.</>}
            </div>
          )}
        </header>

        <article className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {deliverable.status === "failed" && (
              <div
                className="mb-6 border border-[var(--ml-error)]/40 bg-[var(--ml-error)]/5 px-4 py-3 text-sm text-[var(--ml-error)]"
                data-testid="deliverable-failed-banner"
              >
                <span className="ml-mono text-[10px] uppercase tracking-widest block mb-1">Generation failed</span>
                {deliverable.error || "Generation failed."}
              </div>
            )}
            <Markdown>{deliverable.content}</Markdown>
          </div>
          <aside className="lg:col-span-4 no-print">
            <div className="lg:sticky lg:top-24">
              <DeliverableValidationPanel
                deliverable={deliverable}
                canEdit={canEdit}
                busy={deciding}
                onDecide={onDecide}
              />
              <div className="ml-card p-5">
                <div className="ml-eyebrow mb-3">Context Provided</div>
                <p className="text-sm text-[var(--ml-text-secondary)] whitespace-pre-wrap leading-relaxed">
                  {deliverable.context}
                </p>
              </div>
            </div>
          </aside>
        </article>
      </div>
    </div>
  );
}
